import React from 'react';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { 
  Image, 
  Video, 
  Play,
  Eye,
  Edit,
  Trash2,
  Calendar,
  Clock
} from 'lucide-react';

interface GalleryItem {
  id: number;
  title: string;
  description?: string;
  fileKey: string;
  fileName: string;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSize: number;
  url: string;
  urlExpiry: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface GalleryCardProps {
  item: GalleryItem;
  isAdmin?: boolean;
  onView?: (item: GalleryItem) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, title: string) => void;
}

const GalleryCard: React.FC<GalleryCardProps> = ({ 
  item, 
  isAdmin = false,
  onView,
  onEdit,
  onDelete
}) => {
  const FileIcon = item.fileType === 'image' ? Image : Video;

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 border-t-4 border-t-purple-500 overflow-hidden">
      {/* Image/Video Preview */}
      <div className="relative aspect-square overflow-hidden">
        {item.fileType === 'image' ? (
          <img 
            src={item.url} 
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gray-900 flex items-center justify-center relative">
            <video 
              src={item.url}
              className="w-full h-full object-cover"
              muted
              preload="metadata"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                <Play className="h-6 w-6 text-white fill-current" />
              </div>
            </div>
          </div>
        )}
        
        {/* Type Badge */}
        <div className="absolute top-2 right-2">
          <div className={`p-1.5 rounded-full ${
            item.fileType === 'image' 
              ? 'bg-blue-600' 
              : 'bg-purple-600'
          }`}>
            <FileIcon className="h-3 w-3 text-white" />
          </div>
        </div>

        {/* Status Badge (Admin only) */}
        {isAdmin && (
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {item.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        )}
      </div>
      
      <CardContent className="p-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1">
              {item.title}
            </h3>
            {item.description && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {item.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <FileIcon className="h-3 w-3" />
              <span>{item.fileType.toUpperCase()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(item.createdAt).toLocaleDateString()}</span>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <span>Order: {item.order}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{(item.fileSize / 1024 / 1024).toFixed(2)} MB</span>
            <span>•</span>
            <span>{item.mimeType}</span>
          </div>

          {/* Secure access notice for public view */}
          {!isAdmin && (
            <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              <Clock className="h-3 w-3" />
              <span>Secure access - auto-renewed links</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              onClick={() => onView?.(item)}
              size="sm"
              variant="outline"
              className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Eye className="mr-1 h-3 w-3" />
              View
            </Button>
            
            {isAdmin && onEdit && (
              <Button 
                onClick={() => onEdit(item.id)}
                size="sm"
                variant="outline"
                className="border-orange-200 text-orange-600 hover:bg-orange-50"
              >
                <Edit className="mr-1 h-3 w-3" />
                Edit
              </Button>
            )}
            
            {isAdmin && onDelete && (
              <Button 
                onClick={() => onDelete(item.id, item.title)}
                size="sm"
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { GalleryCard };
